import { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { Lock, ChevronDown, ChevronRight, Plus, GripVertical } from "lucide-react";
import type { BudgetSettings, BudgetCategory, BudgetBucket, IncomeRow } from "../../types/budget";
import type { InvestorProfile } from "../../types/entities";
import { currency } from "../../lib/formatting";
import { calculateEffectiveRate, computeBucketAllocations } from "../../lib/calculations";
import { InfoTooltip } from "../ui/InfoTooltip";
import { TabExplainer } from "../ui/TabExplainer";
import { NumberInput } from "../ui/NumberInput";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import type { DragEndEvent } from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

interface BudgetPageProps {
  settings: BudgetSettings;
  categories: BudgetCategory[];
  profile: InvestorProfile;
  onUpdateSettings: (s: BudgetSettings) => void;
  onUpdateCategories: (c: BudgetCategory[]) => void;
  onOpenImport?: (mode: 'replace' | 'merge') => void;
  lastSaved?: string | null;
  initialTab?: "budget" | "buckets";
  appSettings?: import("../settings/SettingsPage").AppSettings;
  onUpdateAppSettings?: (s: import("../settings/SettingsPage").AppSettings) => void;
}

// ─── Constants ────────────────────────────────────────────────────────────────

const FREQ_OPTIONS = ["W", "F", "M", "Q", "Y"] as const;

const PIE_COLORS = [
  "#374151", "#6366f1", "#f59e0b", "#10b981", "#ef4444",
  "#3b82f6", "#8b5cf6", "#ec4899", "#14b8a6", "#f97316",
  "#84cc16", "#06b6d4",
];

// ─── Frequency helpers ────────────────────────────────────────────────────────

function toMonthly(amount: number, freq: string): number {
  if (freq === "W" || freq === "Weekly")                      return (amount * 52) / 12;
  if (freq === "F" || freq === "Fortnightly")                 return (amount * 26) / 12;
  if (freq === "Q" || freq === "Quarterly")                   return amount / 3;
  if (freq === "Y" || freq === "Annual" || freq === "Yearly") return amount / 12;
  return amount;
}

function toAnnual(amount: number, freq: string): number {
  if (freq === "W" || freq === "Weekly")      return amount * 52;
  if (freq === "F" || freq === "Fortnightly") return amount * 26;
  if (freq === "Q" || freq === "Quarterly")   return amount * 4;
  if (freq === "M" || freq === "Monthly")     return amount * 12;
  return amount;
}

function uid(): string {
  return Date.now().toString(36) + Math.random().toString(36).slice(2);
}

// ─── Sortable bucket row ──────────────────────────────────────────────────────

interface SortableBucketRowProps {
  bucket: BudgetBucket;
  isExpanded: boolean;
  onToggle: () => void;
  allocation: { actual: number; fullyFunded: boolean; shortfall: number; requested: number };
  children: React.ReactNode;
  onRemove?: () => void;
}

function SortableBucketRow({ bucket, isExpanded, onToggle, allocation, children, onRemove }: SortableBucketRowProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: bucket.id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 };
  const isLocked = !!bucket.locked;
  return (
    <div ref={setNodeRef} style={style} className="rounded-xl border border-neutral-200 bg-white overflow-hidden">
      {/* Collapsed header */}
      <div className="flex w-full items-center gap-2 px-4 py-3">
        <button {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing touch-manipulation p-1 lg:opacity-0 lg:group-hover/row:opacity-100 transition-opacity" style={{ minWidth: 24, minHeight: 24 }}>
          <GripVertical className="h-4 w-4 text-neutral-300" />
        </button>
        <button onClick={onToggle} className="flex flex-1 items-center gap-3 text-left min-w-0">
          {isExpanded ? <ChevronDown className="h-4 w-4 text-neutral-400 shrink-0" /> : <ChevronRight className="h-4 w-4 text-neutral-400 shrink-0" />}
          {isLocked && <Lock className="h-3.5 w-3.5 text-amber-500 shrink-0" />}
          <span className="flex-1 text-sm font-medium text-neutral-900 truncate">{bucket.name}</span>
        </button>
        <div className="flex items-center gap-2 shrink-0">
          <div className="text-right">
            <span className="text-sm font-semibold text-neutral-700">{currency(Math.round(allocation.actual))}/mo</span>
            {!allocation.fullyFunded && (
              <div className="text-[10px] text-neutral-400">of {currency(Math.round(allocation.requested))} requested</div>
            )}
          </div>
          {!isLocked && onRemove && (
            <button
              onClick={(e) => { e.stopPropagation(); onRemove(); }}
              className="flex h-5 w-5 items-center justify-center rounded-full text-neutral-300 hover:bg-red-50 hover:text-red-500 shrink-0"
            >×</button>
          )}
        </div>
      </div>
      {/* Expanded edit form */}
      {isExpanded && children}
    </div>
  );
}

// ─── Profile auto-row helper ──────────────────────────────────────────────────

function computeAutoRows(profile: InvestorProfile): { rows: IncomeRow[]; amounts: Map<string, number> } {
  const rows: IncomeRow[] = [];
  const amounts = new Map<string, number>();

  type PersonEntry = {
    rowId: string;
    name: string;
    income: number;
    medicareExempt?: boolean;
    hasPrivateHospitalCover?: boolean;
    medicareLevy?: number;
  };

  const people: PersonEntry[] = [
    {
      rowId: "auto-primary",
      name: profile.preferredName || profile.fullLegalName?.split(" ")[0] || "Your",
      income: profile.annualPreTaxIncome ?? 0,
      medicareExempt: profile.medicareExempt,
      hasPrivateHospitalCover: profile.hasPrivateHospitalCover,
      medicareLevy: profile.medicareLevy,
    },
    ...(profile.householdMembers ?? []).map((m) => ({
      rowId: `auto-${m.id}`,
      name: m.preferredName || m.fullLegalName?.split(" ")[0] || "Member",
      income: m.annualPreTaxIncome ?? 0,
      medicareExempt: m.medicareExempt,
      hasPrivateHospitalCover: m.hasPrivateHospitalCover,
      medicareLevy: m.medicareLevy,
    })),
  ];

  for (const person of people) {
    if (person.income <= 0) continue;
    const effectiveRate = calculateEffectiveRate(person.income, {
      medicareExempt: person.medicareExempt,
      hasPrivateHospitalCover: person.hasPrivateHospitalCover,
      medicareLevy: person.medicareLevy,
    });
    const fortnightly = Math.round((person.income * (1 - effectiveRate / 100)) / 26);
    rows.push({ id: person.rowId, label: `${person.name}'s take-home pay`, amount: fortnightly, frequency: "F" });
    amounts.set(person.rowId, fortnightly);
  }

  return { rows, amounts };
}

// ─── SVG pie chart helper ─────────────────────────────────────────────────────

function describeArc(cx: number, cy: number, r: number, startAngle: number, endAngle: number): string {
  const sx = cx + r * Math.sin(startAngle);
  const sy = cy - r * Math.cos(startAngle);
  const ex = cx + r * Math.sin(endAngle);
  const ey = cy - r * Math.cos(endAngle);
  const largeArc = endAngle - startAngle > Math.PI ? 1 : 0;
  return `M ${cx} ${cy} L ${sx.toFixed(3)} ${sy.toFixed(3)} A ${r} ${r} 0 ${largeArc} 1 ${ex.toFixed(3)} ${ey.toFixed(3)} Z`;
}

// ─── Broker grouping (used by export) ────────────────────────────────────────

const BROKER_MAP: Record<string, string> = {
  "Groceries":           "Living Expenses",
  "Health & Beauty":     "Living Expenses",
  "Miscellaneous":       "Living Expenses",
  "Kids":                "Living Expenses",
  "Home":                "Housing",
  "Household Utilities": "Housing",
  "Transport":           "Transport",
  "Insurances":          "Insurance",
  "Banking & Finance":   "Debt Repayments",
  "Investment Property": "Investment Costs",
};
const BROKER_ORDER = ["Living Expenses", "Housing", "Transport", "Insurance", "Debt Repayments", "Investment Costs", "Other"];
function brokerGroup(g: string): string { return BROKER_MAP[g] ?? "Other"; }

// ─── Transaction import helpers ──────────────────────────────────────────────

type ParsedTransaction = {
  date: string;
  description: string;
  amount: number;      // absolute value of expense
  rawAmount: number;   // original (negative = expense)
  autoCategory: string;
};

const KEYWORD_MAP: [string[], string][] = [
  // Supermarkets — most specific first
  [['woolworths','coles','aldi','iga','spar','foodworks','harris farm','greengrocer','butcher','bakery','seafood market','costco','bilo','drakes','foodland','supabarn','ritchies'], 'Supermarkets'],
  // Convenience stores
  [['7-eleven','7eleven','night owl','on the run','bp convenience','coles express','united convenience'], 'Convenience Stores'],
  // Fuel — after convenience so 'coles express' matches convenience first
  [['bp','shell','caltex','ampol','united petroleum','puma energy','liberty oil','metro petroleum','fuel','petrol','diesel','service station'], 'Fuel'],
  // Dining delivery — before restaurants so these match first
  [['uber eats','doordash','menulog','deliveroo','hey you'], 'Restaurants & Dining'],
  // Restaurants & dining
  [['mcdonald','mcdonalds','kfc','hungry jacks','subway','dominos','pizza hut','nandos','grill\'d','guzman','oporto','red rooster','carl jr','burger king','taco bell','betty\'s burgers','shake shack','restaurant','bistro','kitchen','eatery','takeaway','takeout','thai','chinese','indian','japanese','sushi','ramen','pho','kebab'], 'Restaurants & Dining'],
  // Coffee
  [['starbucks','gloria jeans','muffin break','coffee club','zaraffas','hudsons coffee','espresso','barista','roasters','brew','percol','cafe','coffee'], 'Coffee'],
  // Liquor
  [['dan murphy','bws','liquorland','first choice','vintage cellars','bottle shop','bottleshop','liquor','alcohol'], 'Liquor Stores'],
  // Streaming & subscriptions — specific product names first
  [['netflix','spotify','disney plus','disney+','stan','binge','kayo','paramount','apple tv','amazon prime','prime video','youtube premium','audible','kindle','adobe','microsoft 365','office 365','google one','dropbox','icloud','canva','zoom','slack','chatgpt','claude','anthropic','openai'], 'Streaming Services'],
  // Phone & internet
  [['telstra','optus','vodafone','tpg','internode','aussie broadband','superloop','belong','iinet','dodo','amaysim','boost mobile','woolworths mobile','aldimobile','catch connect','circles.life','broadband','mobile plan','phone bill','nbn'], 'Phone & Mobiles'],
  // Electricity & gas
  [['origin energy','agl','energy australia','simply energy','red energy','lumo','alinta','powershop','ergon','synergy','actew','jemena','aurora energy','electricity','gas bill','power bill','energy bill'], 'Electricity & Gas'],
  // Water — before council so water corps match first
  [['sydney water','yarra valley water','melbourne water','unity water','icon water','sa water','water corporation','nt power water','water rates','water bill','waterboard'], 'Water'],
  // Council rates
  [['council rates','city council','shire council','local government','rates notice','land rates','council charge'], 'Rates'],
  // Landlord insurance — more specific than general insurance
  [['landlord insurance','terri scheer','egi insurance','investor guard','rental property insurance'], 'Landlord Insurance'],
  // Health insurance — more specific than general insurance
  [['medibank','bupa','hcf','nib','australian unity','ahm','frank health','defence health','health insurance','private health','hospital cover'], 'Health Insurance'],
  // Life/personal insurance
  [['life insurance','income protection','tpd insurance','trauma insurance','personal insurance'], 'Personal & Life Insurance'],
  // Car insurance — before general insurance
  [['nrma','racq','racv','aami','budget direct','youi','sgic','sgio','car insurance','vehicle insurance','comprehensive insurance','third party'], 'Car & Bike Insurance'],
  // Home & contents insurance
  [['nrma home','aami home','budget direct home','real insurance','allianz home','gi home','youi home','home insurance','contents insurance','home and contents'], 'Home & Contents Insurance'],
  // Pharmacy — before general medical
  [['chemist warehouse','priceline','terry white','amcal','guardian pharmacy','discount drug stores','pharmacy','pharmacist'], 'Medicines & Pharmacy'],
  // Dental
  [['dental','dentist','orthodontist','oral health','tooth','braces'], 'Dentist'],
  // Optical
  [['specsavers','opsm','george and matilda','clearly','optometrist','glasses','contact lens'], 'Eyewear'],
  // Medical / doctors
  [['medicare','bulk bill','medical centre','medical clinic','general practice','physician','specialist','pathology','radiology','x-ray','ultrasound','hospital','emergency','surgical','private hospital','doctor'], 'Doctors'],
  // Gym & fitness
  [['fitness first','goodlife','anytime fitness','snap fitness','virgin active','f45','crossfit','orangetheory','barry\'s','pure barre','pilates','gym','fitness centre','personal trainer','pt session','swim school','swimming','yoga'], 'Gyms & Fitness'],
  // Hair & beauty
  [['hairdresser','blow dry','hair salon','beauty salon','beauty therapist','massage','spa','manicure','pedicure','waxing','tanning','mecca','sephora','haircut','barber','nails'], 'Hair & Beauty'],
  // Clothing
  [['zara','h&m','cotton on','uniqlo','david jones','rivers','jeanswest','country road','seed','witchery','portmans','forever new','factorie','roxy','billabong','quiksilver','the iconic','asos','glue','surf stitch','bonds','the iconic','nike store','adidas store','clothing','fashion','apparel','footwear','sneakers'], 'Clothing & Shoes'],
  // Public transport
  [['opal','myki','go card','metrocard','compass card','translink','ptv','light rail','metro train','commute','transport nsw','transport vic'], 'Public Transport'],
  // Taxi & rideshare
  [['uber','ola','didi','taxify','bolt','ingogo','rideshare','cabcharge','taxi'], 'Taxi & Rideshare'],
  // Parking & tolls
  [['wilson parking','secure parking','care park','linkt','e-toll','citylink','eastlink','transurban','westconnex','toll','parking meter','parking fee'], 'Parking & Tolls'],
  // Vehicle registration
  [['vehicle registration','car rego','registration renewal','service nsw rego','vicroads','transport for nsw','qld transport','service sa'], 'Vehicle Registration'],
  // Vehicle maintenance
  [['mechanic','auto service','car service','logbook service','tyres','tyre','midas','ultra tune','autobarn','supercheap auto','repco','car wash','detailing','roadworthy','rwc'], 'Vehicle Maintenance'],
  // Childcare
  [['childcare','daycare','day care','kindy','kindergarten','creche','after school care','oshc','vacation care','baby bunting'], 'Childcare'],
  // School fees
  [['school fees','tuition','private school','school uniform','excursion','school camp','tuckshop','canteen'], 'School Fees'],
  // Kids activities
  [['swimming lesson','sport registration','dance class','music lesson','netball','football club','soccer club','cricket club','rugby club','junior sport','youth sport'], 'School Needs & Uniforms'],
  // Pet insurance — before general pet
  [['bow wow meow','petsure','woolworths pet','bupa pet','knose','petplan','pet insurance'], 'Pet Insurance'],
  // Vets — before general pet
  [['veterinary clinic','animal hospital','vet clinic','vca animal','greencross vet','veterinary','veterinarian'], 'Vets'],
  // Pet supplies
  [['pet barn','petstock','pet circle','paw','pet food','dog food','cat food','pawsome'], 'Pet Food & Supplies'],
  // Body corporate
  [['body corporate','strata','owners corporation','strata levy','strata fee','community title'], 'Body Corp Fees'],
  // Property management
  [['property management','management fee','rental management','real estate fee','leasing fee','letting fee','trident property','ray white','lj hooker','harcourts','mcgrath','barry plant','nelson alexander','raine horne','century 21','belle property'], 'Other Investment Property'],
  // Mortgage repayments
  [['mortgage','home loan payment','investment loan','property loan','westpac home','commonwealth home','anz home','nab home','ing home','macquarie home'], 'Mortgage Repayments'],
  // Loan repayments
  [['home loan','loan repayment','credit card repayment','personal loan','car loan','afterpay','zip pay','humm','latitude','gem visa'], 'Loan Repayment'],
  // Super & investments
  [['vanguard','blackrock','betashares','commsec','nabtrade','selfwealth','stake','superhero','raiz','spaceship','brickx','comsec pocket','brokerage','superfund','superannuation','hostplus','sunsuper','australian super','aware super','rest super','cbus'], 'Combined Investment Costs'],
  // Tax & accounting
  [['accountant','tax agent','bookkeeper','bas','tax return','myob','xero','quickbooks','h&r block','accounting fee'], 'Tax/Accountancy fees'],
  // Education courses
  [['university','tafe','coursera','udemy','linkedin learning','masterclass','skillshare','textbook','uni fees','hecs','help debt'], 'Course Fees'],
  // Hardware & home improvement
  [['bunnings','mitre 10','total tools','sydney tools','home hardware','thrifty link','hardware','plumber','electrician','handyman','tradesman','tradie','renovation','flooring','tiles','blinds','carpet'], 'Service & Trades'],
  // Cleaning
  [['cleaning service','house clean','cleaner','end of lease clean','bond clean','mrs clean','fantastic cleaners'], 'Cleaning & Other'],
  // Charity
  [['red cross','salvation army','oxfam','world vision','unicef','cancer council','heart foundation','rspca','beyond blue','lifeline','donate','donation','charity','fundraise'], 'Charity'],
  // Banking fees
  [['account keeping fee','monthly fee','annual fee','card fee','atm fee','foreign transaction','bank fee','overdraft fee','dishonour fee','late payment fee'], 'Other Banking & Finance'],
  // General shopping (broad — near end so specific categories match first)
  [['amazon','ebay','kmart','target','big w','harvey norman','jb hi-fi','myer','officeworks','costco'], 'Clothing & Shoes'],
  // Travel & accommodation
  [['airbnb','booking.com','hotels.com','agoda','expedia','wotif','lastminute','qantas hotel','hotel','motel','resort','accommodation','bed and breakfast'], 'Other Miscellaneous'],
  // Flights
  [['qantas','virgin australia','jetstar','rex airlines','bonza','tigerair','air new zealand','singapore airlines','emirates','cathay','flight centre','webjet','airline','airfare'], 'Other Miscellaneous'],
];

function autoMapCategory(description: string): string {
  const lower = description.toLowerCase();
  for (const [keywords, category] of KEYWORD_MAP) {
    if (keywords.some((kw) => lower.includes(kw))) return category;
  }
  return 'Uncategorised';
}

function splitCSVLine(line: string): string[] {
  const result: string[] = [];
  let inQuotes = false;
  let current = '';
  for (const ch of line) {
    if (ch === '"') { inQuotes = !inQuotes; }
    else if (ch === ',' && !inQuotes) { result.push(current); current = ''; }
    else { current += ch; }
  }
  result.push(current);
  return result;
}

function parseCSV(text: string): ParsedTransaction[] {
  const lines = text.trim().split('\n');
  if (lines.length < 2) throw new Error('CSV must have a header row and at least one data row');

  const header = splitCSVLine(lines[0]).map((h) => h.toLowerCase().trim().replace(/"/g, ''));
  const dateIdx = header.findIndex((h) => h.includes('date'));
  const descIdx = header.findIndex((h) => h.includes('desc') || h.includes('narr') || h.includes('detail') || h.includes('memo') || h.includes('particular'));
  const amtIdx  = header.findIndex((h) => h.includes('amount') || h.includes('debit') || h.includes('credit'));

  if (dateIdx === -1 || descIdx === -1 || amtIdx === -1) {
    throw new Error(`Could not find required columns. Found: ${header.join(', ')}. Need: Date, Description, Amount.`);
  }

  const results: ParsedTransaction[] = [];
  for (let i = 1; i < lines.length; i++) {
    const cols = splitCSVLine(lines[i]).map((c) => c.trim().replace(/^"|"$/g, ''));
    if (cols.length <= Math.max(dateIdx, descIdx, amtIdx)) continue;
    const rawAmount = parseFloat((cols[amtIdx] ?? '').replace(/[$,]/g, ''));
    if (isNaN(rawAmount) || rawAmount >= 0) continue;
    const desc = cols[descIdx] ?? '';
    results.push({
      date: cols[dateIdx] ?? '',
      description: desc,
      amount: Math.abs(rawAmount),
      rawAmount,
      autoCategory: autoMapCategory(desc),
    });
  }
  return results;
}

function downloadSampleCSV() {
  const content = `Date,Description,Amount
01/03/2026,Woolworths Supermarkets,-85.50
01/03/2026,Shell Coles Express Fuel,-95.00
02/03/2026,Netflix,-22.99
03/03/2026,Origin Energy,-180.00
04/03/2026,Salary deposit,3500.00`;
  const blob = new Blob([content], { type: 'text/csv' });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href     = url;
  a.download = 'sample-transactions.csv';
  a.click();
  URL.revokeObjectURL(url);
}

// ─── Section header component ─────────────────────────────────────────────────

function SectionHeader({ title, subtitle }: { title: string; subtitle: string }) {
  return (
    <div className="mb-5">
      <h2 className="text-lg font-semibold text-neutral-900">{title}</h2>
      <p className="mt-1 text-sm text-neutral-500">{subtitle}</p>
    </div>
  );
}

// ─── ImportModal component ────────────────────────────────────────────────────

interface ImportModalProps {
  categories: BudgetCategory[];
  onImport: (updates: Record<string, number>) => void;
  onClose: () => void;
}

function ImportModal({ categories, onImport, onClose }: ImportModalProps) {
  const [step, setStep]           = useState<'upload' | 'preview' | 'done'>('upload');
  const [parsedRows, setParsedRows] = useState<ParsedTransaction[]>([]);
  const [mappings, setMappings]   = useState<Record<number, string>>({});
  const [error, setError]         = useState<string | null>(null);

  const categoryNames = Array.from(new Set(categories.map((c) => c.category).filter(Boolean))).sort();

  function handleFile(file: File) {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const text = e.target?.result as string;
        const rows = parseCSV(text);
        if (rows.length === 0) {
          setError('No expense transactions found. The file must contain negative amounts for expenses.');
          return;
        }
        setParsedRows(rows);
        setMappings({});
        setError(null);
        setStep('preview');
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to parse CSV');
      }
    };
    reader.readAsText(file);
  }

  function getMapping(i: number): string {
    return mappings[i] ?? parsedRows[i]?.autoCategory ?? 'Uncategorised';
  }

  function handleImport() {
    const updates: Record<string, number> = {};
    parsedRows.forEach((row, i) => {
      const catName = getMapping(i);
      if (catName === 'Skip' || catName === 'Uncategorised') return;
      updates[catName] = (updates[catName] ?? 0) + row.amount;
    });
    onImport(updates);
    setStep('done');
  }

  const importableCount = parsedRows.filter((_, i) => {
    const m = getMapping(i);
    return m !== 'Skip' && m !== 'Uncategorised';
  }).length;

  const importableTotal = parsedRows.reduce((sum, row, i) => {
    const m = getMapping(i);
    if (m === 'Skip' || m === 'Uncategorised') return sum;
    return sum + row.amount;
  }, 0);

  const mappedCats = new Set(
    parsedRows.map((_, i) => getMapping(i)).filter((m) => m !== 'Skip' && m !== 'Uncategorised')
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-2xl rounded-2xl bg-white shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-neutral-200 px-6 py-4">
          <h2 className="text-base font-semibold text-neutral-800">Import bank transactions</h2>
          <button
            onClick={onClose}
            className="flex h-7 w-7 items-center justify-center rounded-full text-neutral-400 hover:bg-neutral-100 hover:text-neutral-700"
          >
            ✕
          </button>
        </div>

        <div className="p-6">
          {/* ── UPLOAD STEP ── */}
          {step === 'upload' && (
            <div className="space-y-4">
              <p className="text-sm text-neutral-600">
                Upload a CSV export from your bank. The file must have columns for Date, Description (or Narration/Details/Memo/Particulars), and Amount. Expenses should appear as negative amounts.
              </p>
              <div className="flex flex-col items-center gap-3 rounded-xl border-2 border-dashed border-neutral-300 bg-neutral-50 px-6 py-8">
                <p className="text-sm text-neutral-500">Select a CSV file from your bank</p>
                <label className="cursor-pointer rounded-xl bg-neutral-900 px-4 py-2 text-sm font-medium text-white hover:bg-neutral-700">
                  Choose file
                  <input
                    type="file"
                    accept=".csv"
                    className="sr-only"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) handleFile(file);
                    }}
                  />
                </label>
              </div>

              {error && (
                <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                  {error}
                </div>
              )}

              <div className="rounded-xl border border-neutral-200 bg-neutral-50 p-4">
                <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-neutral-500">Expected format</p>
                <pre className="overflow-x-auto text-xs text-neutral-500">{`Date,Description,Amount
01/03/2026,Woolworths,-85.50
01/03/2026,Shell Fuel,-95.00
02/03/2026,Netflix,-22.99`}</pre>
                <button
                  onClick={downloadSampleCSV}
                  className="mt-3 text-xs text-neutral-500 underline hover:text-neutral-700"
                >
                  Download sample CSV
                </button>
              </div>
            </div>
          )}

          {/* ── PREVIEW STEP ── */}
          {step === 'preview' && (
            <div className="space-y-4">
              <p className="text-sm text-neutral-600">
                Review and categorise each transaction. Only expenses (negative amounts) are shown. Positive amounts (income) are skipped automatically.
              </p>

              <div className="max-h-72 overflow-y-auto rounded-xl border border-neutral-200">
                <table className="w-full text-xs">
                  <thead className="sticky top-0 bg-neutral-100">
                    <tr>
                      <th className="px-3 py-2 text-left font-medium text-neutral-500">Date</th>
                      <th className="px-3 py-2 text-left font-medium text-neutral-500">Description</th>
                      <th className="px-3 py-2 text-right font-medium text-neutral-500">Amount</th>
                      <th className="px-3 py-2 text-left font-medium text-neutral-500">Category</th>
                    </tr>
                  </thead>
                  <tbody>
                    {parsedRows.slice(0, 50).map((row, i) => (
                      <tr key={i} className="border-t border-neutral-100 hover:bg-neutral-50">
                        <td className="px-3 py-1.5 text-neutral-500">{row.date}</td>
                        <td className="px-3 py-1.5 text-neutral-700">{row.description.length > 40 ? row.description.slice(0, 40) + '…' : row.description}</td>
                        <td className="px-3 py-1.5 text-right text-neutral-700">${row.amount.toFixed(2)}</td>
                        <td className="px-3 py-1.5">
                          <select
                            value={getMapping(i)}
                            onChange={(e) => setMappings((prev) => ({ ...prev, [i]: e.target.value }))}
                            className="rounded border border-neutral-200 px-1.5 py-0.5 text-xs text-neutral-700 focus:outline-none focus:ring-1 focus:ring-neutral-300"
                          >
                            {categoryNames.map((name) => (
                              <option key={name} value={name}>{name}</option>
                            ))}
                            <option value="Uncategorised">Uncategorised</option>
                            <option value="Skip">Skip this transaction</option>
                          </select>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {parsedRows.length > 50 && (
                  <p className="px-3 py-2 text-xs text-neutral-400">Showing first 50 of {parsedRows.length} transactions.</p>
                )}
              </div>

              <div className="rounded-xl bg-neutral-50 px-4 py-3 text-sm text-neutral-600">
                <span className="font-medium">{importableCount} transactions</span> · ${importableTotal.toFixed(2)} total · mapped to <span className="font-medium">{mappedCats.size} categories</span>
              </div>

              <div className="flex gap-2">
                <button
                  onClick={handleImport}
                  disabled={importableCount === 0}
                  className="rounded-xl bg-neutral-900 px-4 py-2 text-sm font-medium text-white hover:bg-neutral-700 disabled:opacity-40"
                >
                  Import {importableCount} transaction{importableCount !== 1 ? 's' : ''}
                </button>
                <button
                  onClick={() => { setStep('upload'); setParsedRows([]); setMappings({}); setError(null); }}
                  className="rounded-xl border border-neutral-200 px-4 py-2 text-sm text-neutral-600 hover:bg-neutral-50"
                >
                  Back
                </button>
              </div>
            </div>
          )}

          {/* ── DONE STEP ── */}
          {step === 'done' && (
            <div className="space-y-4 text-center">
              <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-emerald-100 text-2xl">
                ✓
              </div>
              <div>
                <p className="text-base font-semibold text-neutral-900">Transactions imported</p>
                <p className="mt-1 text-sm text-neutral-500">
                  Actual spend has been updated across {mappedCats.size} budget categories.
                </p>
              </div>
              <button
                onClick={onClose}
                className="rounded-xl bg-neutral-900 px-6 py-2 text-sm font-medium text-white hover:bg-neutral-700"
              >
                Done
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

function formatLastSaved(iso: string | null | undefined): string {
  if (!iso) return "";
  const diff = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (diff < 5) return "just now";
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return new Date(iso).toLocaleDateString("en-AU", { day: "numeric", month: "short" });
}

export default function BudgetPage({
  settings,
  categories,
  profile,
  onUpdateSettings,
  onUpdateCategories,
  onOpenImport,
  lastSaved,
  initialTab,
  appSettings,
  onUpdateAppSettings,
}: BudgetPageProps) {

  const [activeTab, setActiveTab] = useState<"budget" | "buckets">(initialTab ?? "budget");
  const [expandedBucketId, setExpandedBucketId] = useState<string | null>(null);

  // ── Auto-rows: seed income rows from profile if none exist (one-time) ──────
  const autoRowsSeeded = useRef(false);
  useEffect(() => {
    if (autoRowsSeeded.current) return;
    const hasAutoRows = (settings.incomeRows ?? []).some(
      (r) => r.id === "auto-primary" || r.id.startsWith("auto-")
    );
    if (!hasAutoRows) {
      const { rows } = computeAutoRows(profile);
      if (rows.length > 0) {
        autoRowsSeeded.current = true;
        onUpdateSettings({ ...settings, incomeRows: rows });
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [collapsedGroups, setCollapsedGroups] = useState<Record<string, boolean>>({});
  const [profileAutoRows] = useState<Map<string, number>>(() => {
    const hasAutoRows = (settings.incomeRows ?? []).some(
      (r) => r.id === "auto-primary" || r.id.startsWith("auto-")
    );
    if (!hasAutoRows) {
      const { amounts } = computeAutoRows(profile);
      return amounts;
    }
    return new Map();
  });

  // Export state
  const [showExportModal, setShowExportModal] = useState(false);
  const [printMode, setPrintMode] = useState(false);

  // Import state
  const [showImportModal, setShowImportModal] = useState(false);
  const [showBudgetImportModal, setShowBudgetImportModal] = useState(false);
  const [exportFmt, setExportFmt]           = useState({ pdf: true, csv: false });
  const [exportGrouping, setExportGrouping] = useState<"current" | "broker" | "custom">("current");
  const [exportCols, setExportCols]         = useState({ budget: true, actual: true, variance: true, notes: false, annual: true });
  const [exportSections, setExportSections] = useState({ income: true, expenses: true, buckets: true });

  // ── Core update helpers (props are source of truth — no local state copy) ───

  function updateSettings(updates: Partial<BudgetSettings>) {
    onUpdateSettings({ ...settings, ...updates });
  }

  function updateCats(updated: BudgetCategory[]) {
    onUpdateCategories(updated);
  }

  // ── Income rows ──────────────────────────────────────────────────────────────

  const incomeRows = settings.incomeRows ?? [];

  function updateIncomeRow(id: string, updates: Partial<IncomeRow>) {
    updateSettings({ incomeRows: incomeRows.map((r) => (r.id === id ? { ...r, ...updates } : r)) });
  }
  function addIncomeRow() {
    updateSettings({ incomeRows: [...incomeRows, { id: uid(), label: "", amount: 0, frequency: "F" }] });
  }
  function removeIncomeRow(id: string) {
    updateSettings({ incomeRows: incomeRows.filter((r) => r.id !== id) });
  }

  // ── Categories ───────────────────────────────────────────────────────────────

  function updateCategory(id: string, updates: Partial<BudgetCategory>) {
    updateCats(categories.map((c) => (c.id === id ? { ...c, ...updates } : c)));
  }
  function addCategoryToGroup(groupId: string) {
    updateCats([...categories, { id: uid(), category: "", budget: 0, actual: 0, frequency: "M", groupId, notes: "" }]);
  }
  function removeCategory(id: string) {
    updateCats(categories.filter((c) => c.id !== id));
  }
  function handleClearActuals() {
    if (!confirm('Reset all actual spend to $0?')) return;
    updateCats(categories.map((cat) => ({ ...cat, actual: 0 })));
  }

  // ── Groups ───────────────────────────────────────────────────────────────────

  const sortedGroups = [...settings.groups].sort((a, b) => a.sortOrder - b.sortOrder);

  function addGroup() {
    const maxSort = settings.groups.reduce((m, g) => Math.max(m, g.sortOrder), 0);
    updateSettings({ groups: [...settings.groups, { id: uid(), name: "New Group", sortOrder: maxSort + 1 }] });
  }
  function removeGroup(groupId: string) {
    const group = settings.groups.find((g) => g.id === groupId);
    const count = categories.filter((c) => c.groupId === groupId).length;
    if (count > 0 && !window.confirm(`Remove "${group?.name ?? groupId}" and its ${count} item${count === 1 ? "" : "s"}?`)) return;
    updateCats(categories.filter((c) => c.groupId !== groupId));
    updateSettings({ groups: settings.groups.filter((g) => g.id !== groupId) });
  }
  function renameGroup(groupId: string, newName: string) {
    const group = settings.groups.find((g) => g.id === groupId);
    if (!newName.trim() || !group || newName === group.name) return;
    updateSettings({ groups: settings.groups.map((g) => (g.id === groupId ? { ...g, name: newName } : g)) });
  }

  // ── Buckets ──────────────────────────────────────────────────────────────────

  function updateBucket(id: string, updates: Partial<BudgetBucket>) {
    updateSettings({ buckets: settings.buckets.map((b) => (b.id === id ? { ...b, ...updates } : b)) });
  }
  function addBucket() {
    const nb: BudgetBucket = { id: uid(), name: "", description: "", type: "custom", monthlyAmount: 0 };
    updateSettings({ buckets: [...settings.buckets, nb] });
  }
  function removeBucket(id: string) {
    updateSettings({ buckets: settings.buckets.filter((b) => b.id !== id) });
  }

  // ── Derived values ───────────────────────────────────────────────────────────

  const totalMonthlyIncome   = incomeRows.reduce((s, r) => s + toMonthly(r.amount, r.frequency), 0);
  const totalMonthlyExpenses = categories.reduce((s, c) => s + toMonthly(c.budget, c.frequency), 0);
  const totalActualExpenses  = categories.reduce((s, c) => s + (c.actual ?? 0), 0);
  const hasAnyActuals        = totalActualExpenses > 0;
  const monthlySurplus       = totalMonthlyIncome - totalMonthlyExpenses;
  const bucketAllocations    = computeBucketAllocations(settings.buckets, monthlySurplus);
  const totalAllocated       = settings.buckets.reduce((s, b) => s + b.monthlyAmount, 0);
  const totalActuallyFunded  = bucketAllocations.reduce((s, a) => s + a.actual, 0);
  const remaining            = Math.max(0, monthlySurplus - totalActuallyFunded);

  const showAdvisoryNote      = profileAutoRows.size > 0;

  const allocatedBucketCount  = settings.buckets.filter((b) => b.monthlyAmount > 0).length;

  // ── DnD sensors for bucket reorder ──
  const bucketSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );
  function handleBucketDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = settings.buckets.findIndex((b) => b.id === active.id);
    const newIndex = settings.buckets.findIndex((b) => b.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;
    updateSettings({ buckets: arrayMove(settings.buckets, oldIndex, newIndex) });
  }

  // Group name lookup for display and broker export
  const groupNameById = new Map(settings.groups.map((g) => [g.id, g.name]));

  // Pie chart data — groups with monthly spend > 0, in sort order
  const pieData = sortedGroups
    .map((g) => ({
      id:      g.id,
      name:    g.name,
      monthly: categories.filter((c) => c.groupId === g.id).reduce((s, c) => s + toMonthly(c.budget, c.frequency), 0),
    }))
    .filter((d) => d.monthly > 0);

  let _cumAngle = 0;
  const pieSegments = pieData.map((d) => {
    const angle      = totalMonthlyExpenses > 0 ? (d.monthly / totalMonthlyExpenses) * 2 * Math.PI : 0;
    const startAngle = _cumAngle;
    const endAngle   = _cumAngle + angle;
    _cumAngle        = endAngle;
    return { ...d, startAngle, endAngle, pct: Math.round((d.monthly / totalMonthlyExpenses) * 100) };
  });

  // ── Export: CSV ──────────────────────────────────────────────────────────────

  function exportCSV() {
    const esc = (v: string | number) => {
      const s = String(v);
      return s.includes(",") || s.includes('"') || s.includes("\n") ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const lines: string[] = [];

    if (exportSections.income) {
      lines.push("INCOME");
      lines.push(["Label", "Amount", "Frequency", "Monthly", "Annual"].map(esc).join(","));
      for (const r of incomeRows) {
        lines.push([r.label, r.amount, r.frequency,
          toMonthly(r.amount, r.frequency).toFixed(2),
          toAnnual(r.amount, r.frequency).toFixed(2)].map(esc).join(","));
      }
      lines.push(["TOTAL INCOME", "", "", totalMonthlyIncome.toFixed(2), (totalMonthlyIncome * 12).toFixed(2)].map(esc).join(","));
      lines.push("");
    }

    if (exportSections.expenses) {
      const hdrs = ["Category"];
      if (exportCols.budget)   hdrs.push("Budget");
      if (exportCols.actual)   hdrs.push("Actual");
      hdrs.push("Frequency");
      if (exportCols.annual)   hdrs.push("Annual");
      if (exportCols.variance) hdrs.push("Variance");
      if (exportCols.notes)    hdrs.push("Notes");

      lines.push("EXPENSES");
      lines.push(hdrs.map(esc).join(","));

      const groupsToRender =
        exportGrouping === "broker"
          ? BROKER_ORDER.map((bg) => ({ name: bg, cats: categories.filter((c) => brokerGroup(groupNameById.get(c.groupId) ?? "") === bg) })).filter((g) => g.cats.length > 0)
          : sortedGroups.map((g) => ({ name: g.name, cats: categories.filter((c) => c.groupId === g.id) }));

      for (const grp of groupsToRender) {
        lines.push([grp.name].map(esc).join(","));
        for (const c of grp.cats) {
          const row = [c.category];
          if (exportCols.budget)   row.push(c.budget.toString());
          if (exportCols.actual)   row.push(c.actual.toString());
          row.push(c.frequency);
          if (exportCols.annual)   row.push(toAnnual(c.budget, c.frequency).toFixed(2));
          if (exportCols.variance) row.push((toMonthly(c.budget, c.frequency) - toMonthly(c.actual, c.frequency)).toFixed(2));
          if (exportCols.notes)    row.push(c.notes ?? "");
          lines.push(row.map(esc).join(","));
        }
        const sub = grp.cats.reduce((s, c) => s + toMonthly(c.budget, c.frequency), 0);
        lines.push([`${grp.name} subtotal`, "", "", "", (sub * 12).toFixed(2)].map(esc).join(","));
        lines.push("");
      }
      lines.push(["TOTAL EXPENSES", "", "", totalMonthlyExpenses.toFixed(2), (totalMonthlyExpenses * 12).toFixed(2)].map(esc).join(","));
      lines.push("");
    }

    if (exportSections.buckets) {
      lines.push("SAVINGS / BUCKETS");
      lines.push(["Bucket", "Description", "Monthly"].map(esc).join(","));
      for (const b of settings.buckets) {
        lines.push([b.name, b.description ?? "", b.monthlyAmount.toString()].map(esc).join(","));
      }
      lines.push("");
    }

    const blob = new Blob([lines.join("\n")], { type: "text/csv" });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement("a");
    a.href = url;
    a.download = `budget-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    setShowExportModal(false);
  }

  // ── Export: PDF ──────────────────────────────────────────────────────────────

  function exportPDF() {
    setPrintMode(true);
    setTimeout(() => {
      document.documentElement.classList.add("budget-print-mode");
      window.addEventListener("afterprint", function cleanup() {
        document.documentElement.classList.remove("budget-print-mode");
        setPrintMode(false);
        window.removeEventListener("afterprint", cleanup);
      });
      window.print();
    }, 60);
    setShowExportModal(false);
  }

  // ── Print portal ─────────────────────────────────────────────────────────────

  const printArea = printMode
    ? createPortal(
        <div id="budget-print-portal" style={{ fontFamily: "sans-serif", padding: "24px", background: "white", color: "#111" }}>
          <h1 style={{ fontSize: "20px", marginBottom: "4px" }}>Budget Summary</h1>
          <p style={{ fontSize: "12px", color: "#666", marginBottom: "20px" }}>
            {new Date().toLocaleDateString("en-AU", { day: "numeric", month: "long", year: "numeric" })}
          </p>
          {exportSections.income && (
            <section style={{ marginBottom: "20px" }}>
              <h2 style={{ fontSize: "14px", fontWeight: 700, borderBottom: "1px solid #ddd", paddingBottom: "4px", marginBottom: "8px" }}>Income</h2>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "12px" }}>
                <thead>
                  <tr style={{ background: "#f5f5f5" }}>
                    <th style={{ textAlign: "left", padding: "4px 8px" }}>Label</th>
                    <th style={{ textAlign: "right", padding: "4px 8px" }}>Monthly</th>
                    <th style={{ textAlign: "right", padding: "4px 8px" }}>Annual</th>
                  </tr>
                </thead>
                <tbody>
                  {incomeRows.map((r) => (
                    <tr key={r.id}>
                      <td style={{ padding: "3px 8px" }}>{r.label || "—"}</td>
                      <td style={{ textAlign: "right", padding: "3px 8px" }}>{currency(Math.round(toMonthly(r.amount, r.frequency)))}</td>
                      <td style={{ textAlign: "right", padding: "3px 8px" }}>{currency(Math.round(toAnnual(r.amount, r.frequency)))}</td>
                    </tr>
                  ))}
                  <tr style={{ fontWeight: 700, borderTop: "1px solid #ddd" }}>
                    <td style={{ padding: "4px 8px" }}>Total</td>
                    <td style={{ textAlign: "right", padding: "4px 8px" }}>{currency(Math.round(totalMonthlyIncome))}</td>
                    <td style={{ textAlign: "right", padding: "4px 8px" }}>{currency(Math.round(totalMonthlyIncome * 12))}</td>
                  </tr>
                </tbody>
              </table>
            </section>
          )}
          {exportSections.expenses && (
            <section style={{ marginBottom: "20px" }}>
              <h2 style={{ fontSize: "14px", fontWeight: 700, borderBottom: "1px solid #ddd", paddingBottom: "4px", marginBottom: "8px" }}>Expenses</h2>
              {sortedGroups.map((g) => {
                const grpCats    = categories.filter((c) => c.groupId === g.id);
                const grpMonthly = grpCats.reduce((s, c) => s + toMonthly(c.budget, c.frequency), 0);
                if (grpCats.length === 0) return null;
                return (
                  <div key={g.id} style={{ marginBottom: "10px" }}>
                    <div style={{ fontSize: "11px", fontWeight: 700, background: "#f0f0f0", padding: "3px 8px", display: "flex", justifyContent: "space-between" }}>
                      <span>{g.name}</span><span>{currency(Math.round(grpMonthly))}/mo</span>
                    </div>
                    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "11px" }}>
                      <tbody>
                        {grpCats.map((c) => (
                          <tr key={c.id}>
                            <td style={{ padding: "2px 8px" }}>{c.category}</td>
                            {exportCols.budget && <td style={{ textAlign: "right", padding: "2px 8px" }}>{currency(c.budget)} {c.frequency}</td>}
                            {exportCols.annual && <td style={{ textAlign: "right", padding: "2px 8px" }}>{currency(Math.round(toAnnual(c.budget, c.frequency)))}/yr</td>}
                            {exportCols.notes  && <td style={{ padding: "2px 8px", color: "#666" }}>{c.notes}</td>}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                );
              })}
              <div style={{ fontWeight: 700, fontSize: "12px", borderTop: "2px solid #333", paddingTop: "4px", display: "flex", justifyContent: "space-between" }}>
                <span>Total Expenses</span>
                <span>{currency(Math.round(totalMonthlyExpenses))}/mo · {currency(Math.round(totalMonthlyExpenses * 12))}/yr</span>
              </div>
            </section>
          )}
          {exportSections.buckets && settings.buckets.length > 0 && (
            <section>
              <h2 style={{ fontSize: "14px", fontWeight: 700, borderBottom: "1px solid #ddd", paddingBottom: "4px", marginBottom: "8px" }}>Savings / Buckets</h2>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "12px" }}>
                <tbody>
                  {settings.buckets.map((b) => (
                    <tr key={b.id}>
                      <td style={{ padding: "3px 8px" }}>{b.name || "—"}</td>
                      <td style={{ padding: "3px 8px", color: "#666", fontSize: "11px" }}>{b.description}</td>
                      <td style={{ textAlign: "right", padding: "3px 8px" }}>{currency(b.monthlyAmount)}/mo</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
          )}
        </div>,
        document.body
      )
    : null;

  // ── Render ───────────────────────────────────────────────────────────────────

  return (
    <>
      <style dangerouslySetInnerHTML={{ __html: `
        @media print {
          html.budget-print-mode body > :not(#budget-print-portal) { display: none !important; }
          html.budget-print-mode #budget-print-portal { display: block !important; }
        }
      ` }} />

      {printArea}

      <div className="space-y-8">

        {/* ── PAGE HEADER ──────────────────────────────────────────────────────── */}
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-neutral-900">Personal Budget</h1>
            <p className="mt-1 text-sm text-neutral-500">
              Track income, expenses, and how you allocate your surplus each month.
              {lastSaved && (
                <span className="ml-2 text-xs text-neutral-400">Last saved: {formatLastSaved(lastSaved)}</span>
              )}
            </p>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            {onOpenImport && (
              <button
                onClick={() => setShowBudgetImportModal(true)}
                className="rounded-lg border border-neutral-200 bg-white px-3 py-1.5 text-xs font-medium text-neutral-700 hover:bg-neutral-50"
              >
                Import from spreadsheet
              </button>
            )}
            <button
              onClick={() => setShowImportModal(true)}
              className="rounded-lg border border-neutral-200 bg-white px-3 py-1.5 text-xs font-medium text-neutral-700 hover:bg-neutral-50"
            >
              Import transactions
            </button>
            <button
              onClick={handleClearActuals}
              className="rounded-lg border border-neutral-200 bg-white px-3 py-1.5 text-xs font-medium text-neutral-400 hover:text-neutral-700 hover:bg-neutral-50"
            >
              Clear actuals
            </button>
            <button
              onClick={() => setShowExportModal(true)}
              className="rounded-xl border border-neutral-200 bg-white px-4 py-2 text-sm font-medium text-neutral-700 hover:bg-neutral-50"
            >
              Export budget
            </button>
          </div>
        </div>

        <TabExplainer>
          <p>
            <strong>Personal Budget — Your monthly cashflow before property</strong>
          </p>
          <p>
            This page is the foundation everything else builds on. Get your income and expenses right
            here, and the surplus flowing into your portfolio (and into your savings buckets) will be
            accurate everywhere it appears.
          </p>
          <p>
            <strong>Income tab</strong> — All money coming in: salary (pre-tax), partner's salary,
            side income, dividends, distributions. Used to calculate your monthly surplus
            (income - expenses) which feeds your funding capacity.
          </p>
          <p>
            <strong>Expenses tab</strong> — Personal living costs only: groceries, utilities, mortgage
            on your home, transport, insurance, subscriptions. Do NOT include investment property
            expenses here — those are tracked separately on each property's page.
          </p>
          <p>
            <strong>Buckets tab</strong> — How your monthly surplus is allocated. Buckets are funded
            in priority order (drag to reorder). The Property Portfolio bucket flows into Funding
            Sources on the Cashflow & Transfers page; non-property buckets (holidays, splurge, savings)
            flow into Bucket Flow. If your surplus runs out before the last bucket, that bucket gets
            partially funded or zero — see "Funded vs Requested" indicators on each card.
          </p>
          <p>
            <strong>Why this matters:</strong> the surplus calculated here is the source of truth for
            everything downstream — portfolio funding capacity, bucket allocations, AI advice. Sloppy
            numbers here propagate.
          </p>
          <p className="text-amber-700">
            All figures are monthly. Annual amounts (insurance, registration, rates) should be entered
            as monthly equivalents — divide annual / 12.
          </p>
        </TabExplainer>

        {/* Tab bar */}
        <div className="flex gap-6 border-b border-neutral-200 mb-6">
          {(["budget", "buckets"] as const).map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`pb-2 text-sm font-medium transition-colors border-b-2 ${
                activeTab === tab
                  ? "border-neutral-900 text-neutral-900"
                  : "border-transparent text-neutral-500 hover:text-neutral-700"
              }`}
            >
              {tab === "budget" ? "Personal Budget" : "Buckets"}
            </button>
          ))}
        </div>

        {activeTab === "budget" && (<>
        {/* ── 1. INCOME ────────────────────────────────────────────────────────── */}
        <div className="rounded-2xl bg-neutral-50 p-5">
          <SectionHeader
            title="Income"
            subtitle="Personal income only — salary and wages. Rental and investment income is tracked separately in your Portfolio. Adjust the frequency to match how you actually get paid."
          />

          {/* Advisory note */}
          {showAdvisoryNote && (
            <div className="mb-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
              Income shown is estimated take-home pay based on your tax settings in My Profile. Confirm each amount matches your actual payslip and adjust if needed.
            </div>
          )}

          {/* Table header */}
          <div className="hidden grid-cols-[2fr_1fr_auto_1fr_1fr_auto] gap-x-3 border-b border-neutral-200 pb-2 text-xs font-medium uppercase tracking-wide text-neutral-400 md:grid">
            <div>Label</div><div>Amount</div><div>Freq</div><div>Monthly</div><div>Annual</div><div />
          </div>

          <div className="mt-2 space-y-1.5">
            {incomeRows.map((row) => (
              <div key={row.id} className="grid items-start gap-x-3 rounded-xl bg-white px-3 py-2 md:grid-cols-[2fr_1fr_auto_1fr_1fr_auto]">
                <div>
                  <input
                    type="text"
                    value={row.label}
                    placeholder="Income label"
                    onChange={(e) => updateIncomeRow(row.id, { label: e.target.value })}
                    className="w-full rounded-lg border border-neutral-200 px-3 py-1.5 text-sm text-neutral-900 focus:outline-none focus:ring-2 focus:ring-neutral-300"
                  />
                  {profileAutoRows.has(row.id) && (
                    row.amount === profileAutoRows.get(row.id)
                      ? <span className="mt-0.5 block text-xs italic text-amber-500 pl-1">est. take-home</span>
                      : <span className="mt-0.5 block text-xs italic text-neutral-400 pl-1">adjusted</span>
                  )}
                </div>
                <div className="flex items-center gap-1">
                  <span className="text-xs text-neutral-400">$</span>
                  <NumberInput min={0}
                    value={row.amount || ""}
                    placeholder="0"
                    onChange={(e) => updateIncomeRow(row.id, { amount: parseFloat(e.target.value) || 0 })}
                    className="w-full rounded-lg border border-neutral-200 px-2 py-1.5 text-sm text-neutral-900 focus:outline-none focus:ring-2 focus:ring-neutral-300"
                  />
                </div>
                <select
                  value={row.frequency}
                  onChange={(e) => updateIncomeRow(row.id, { frequency: e.target.value })}
                  className="rounded-lg border border-neutral-200 px-2 py-1.5 text-sm text-neutral-700 focus:outline-none"
                >
                  {FREQ_OPTIONS.map((f) => <option key={f} value={f}>{f}</option>)}
                </select>
                <div className="py-1.5 text-sm text-neutral-600">
                  {currency(Math.round(toMonthly(row.amount, row.frequency)))}
                  <span className="ml-1 text-xs text-neutral-400">/mo</span>
                </div>
                <div className="py-1.5 text-sm text-neutral-600">
                  {currency(Math.round(toAnnual(row.amount, row.frequency)))}
                  <span className="ml-1 text-xs text-neutral-400">/yr</span>
                </div>
                <button
                  onClick={() => removeIncomeRow(row.id)}
                  className="flex h-7 w-6 items-center justify-center rounded-full text-neutral-300 hover:bg-red-50 hover:text-red-500"
                >×</button>
              </div>
            ))}
          </div>

          {/* Income totals row */}
          {incomeRows.length > 0 && (
            <div className="mt-2 grid items-center gap-x-3 rounded-xl border border-neutral-200 bg-neutral-100 px-3 py-2 md:grid-cols-[2fr_1fr_auto_1fr_1fr_auto]">
              <div className="text-xs font-semibold uppercase tracking-wide text-neutral-500">Total</div>
              <div /><div />
              <div className="text-sm font-semibold text-neutral-800">
                {currency(Math.round(totalMonthlyIncome))}<span className="ml-1 text-xs font-normal text-neutral-400">/mo</span>
              </div>
              <div className="text-sm font-semibold text-neutral-800">
                {currency(Math.round(totalMonthlyIncome * 12))}<span className="ml-1 text-xs font-normal text-neutral-400">/yr</span>
              </div>
              <div />
            </div>
          )}

          <button
            onClick={addIncomeRow}
            className="mt-3 w-full rounded-xl border border-dashed border-neutral-300 py-2 text-sm text-neutral-500 hover:border-neutral-400 hover:text-neutral-700"
          >
            + Add income row
          </button>
        </div>

        {/* ── 2. EXPENSES ──────────────────────────────────────────────────────── */}
        <div className="rounded-2xl bg-neutral-50 p-5">
          <SectionHeader
            title="Expenses"
            subtitle="Your day-to-day costs grouped by category. Edit any amount inline — the totals update automatically. Collapse groups you've reviewed to focus on what matters."
          />

          <div className="space-y-4">
            {sortedGroups.map((group) => {
              const groupCats       = categories.filter((c) => c.groupId === group.id);
              const grpMonthly      = groupCats.reduce((s, c) => s + toMonthly(c.budget, c.frequency), 0);
              const grpAnnual       = grpMonthly * 12;
              const grpActualTotal  = groupCats.reduce((s, c) => s + (c.actual ?? 0), 0);
              const hasGroupActuals = groupCats.some((c) => (c.actual ?? 0) > 0);
              const isCollapsed = !!collapsedGroups[group.id];

              return (
                <div key={group.id} className="overflow-hidden rounded-2xl border border-neutral-200 bg-white">
                  {/* Group header */}
                  <div className="flex items-center gap-2 bg-neutral-100 px-4 py-2.5">
                    <input
                      key={group.id}
                      defaultValue={group.name}
                      onBlur={(e) => {
                        const v = e.target.value.trim();
                        if (!v) { e.target.value = group.name; return; }
                        renameGroup(group.id, v);
                      }}
                      className="min-w-0 flex-1 bg-transparent text-sm font-semibold text-neutral-800 focus:outline-none"
                    />
                    <span className="shrink-0 text-xs text-neutral-500">
                      {currency(Math.round(grpMonthly))}/mo · {currency(Math.round(grpAnnual))}/yr
                    </span>
                    <button
                      onClick={() => addCategoryToGroup(group.id)}
                      className="shrink-0 rounded-lg border border-neutral-300 bg-white px-2 py-0.5 text-xs text-neutral-600 hover:bg-neutral-50"
                    >+ Add item</button>
                    <button
                      onClick={() => setCollapsedGroups((p) => ({ ...p, [group.id]: !p[group.id] }))}
                      className="w-5 shrink-0 text-center text-sm text-neutral-400 hover:text-neutral-700"
                    >{isCollapsed ? "▶" : "▼"}</button>
                    <button
                      onClick={() => removeGroup(group.id)}
                      className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-neutral-300 hover:bg-red-50 hover:text-red-500"
                    >×</button>
                  </div>

                  {!isCollapsed && (
                    <div className="px-3 py-2">
                      {/* Item header */}
                      <div className="hidden grid-cols-[2fr_1fr_1fr_auto_1fr_1fr_1.5fr_auto] gap-x-2 border-b border-neutral-100 pb-1.5 text-xs font-medium uppercase tracking-wide text-neutral-400 md:grid">
                        <div>Category</div><div>Budget</div><div>Actual</div><div>Freq</div>
                        <div>Monthly</div><div>Annual</div><div>Notes</div><div />
                      </div>

                      <div className="mt-1.5 space-y-1">
                        {groupCats.map((item) => (
                          <div key={item.id} className="grid items-center gap-x-2 rounded-lg px-2 py-1.5 hover:bg-neutral-50 md:grid-cols-[2fr_1fr_1fr_auto_1fr_1fr_1.5fr_auto]">
                            <input
                              type="text" value={item.category} placeholder="Item name"
                              onChange={(e) => updateCategory(item.id, { category: e.target.value })}
                              className="rounded border border-neutral-200 px-2 py-1 text-sm text-neutral-900 focus:outline-none focus:ring-1 focus:ring-neutral-300"
                            />
                            <div className="flex items-center gap-0.5">
                              <span className="text-xs text-neutral-400">$</span>
                              <NumberInput min={0} value={item.budget || ""} placeholder="0"
                                onChange={(e) => updateCategory(item.id, { budget: parseFloat(e.target.value) || 0 })}
                                className="w-full rounded border border-neutral-200 px-1.5 py-1 text-sm text-neutral-900 focus:outline-none focus:ring-1 focus:ring-neutral-300"
                              />
                            </div>
                            <div className="flex flex-col gap-0.5">
                              <div className="flex items-center gap-0.5">
                                <span className="text-xs text-neutral-400">$</span>
                                <NumberInput min={0} value={item.actual || ""} placeholder="0"
                                  onChange={(e) => updateCategory(item.id, { actual: parseFloat(e.target.value) || 0 })}
                                  className={`w-full rounded border px-1.5 py-1 text-sm text-neutral-900 focus:outline-none focus:ring-1 focus:ring-neutral-300 ${item.actual > 0 && item.actual > item.budget ? 'bg-red-50 border-red-300' : item.actual > 0 && item.actual < item.budget ? 'bg-green-50 border-green-300' : 'border-neutral-200'}`}
                                />
                              </div>
                              {item.actual > 0 && item.actual !== item.budget && (
                                <span className={`text-xs whitespace-nowrap ${item.actual > item.budget ? 'text-red-600' : 'text-emerald-600'}`}>
                                  {item.actual > item.budget
                                    ? `+${currency(item.actual - item.budget)} over`
                                    : `−${currency(item.budget - item.actual)} under`}
                                </span>
                              )}
                            </div>
                            <select
                              value={item.frequency}
                              onChange={(e) => updateCategory(item.id, { frequency: e.target.value })}
                              className="rounded border border-neutral-200 px-1.5 py-1 text-sm text-neutral-700 focus:outline-none"
                            >
                              {FREQ_OPTIONS.map((f) => <option key={f} value={f}>{f}</option>)}
                            </select>
                            <div className="text-sm text-neutral-600">
                              {currency(Math.round(toMonthly(item.budget, item.frequency)))}
                              <span className="ml-0.5 text-xs text-neutral-400">/mo</span>
                            </div>
                            <div className="text-sm text-neutral-600">
                              {currency(Math.round(toAnnual(item.budget, item.frequency)))}
                              <span className="ml-0.5 text-xs text-neutral-400">/yr</span>
                            </div>
                            <input
                              type="text" value={item.notes ?? ""} placeholder="Notes…"
                              onChange={(e) => updateCategory(item.id, { notes: e.target.value })}
                              className="rounded border border-neutral-200 px-2 py-1 text-xs text-neutral-500 focus:outline-none focus:ring-1 focus:ring-neutral-300"
                            />
                            <button
                              onClick={() => removeCategory(item.id)}
                              className="flex h-5 w-5 items-center justify-center rounded-full text-neutral-300 hover:bg-red-50 hover:text-red-500"
                            >×</button>
                          </div>
                        ))}
                      </div>

                      {/* Group subtotal */}
                      {groupCats.length > 0 && (
                        <div className="mt-1 grid items-center gap-x-2 rounded-lg bg-neutral-50 px-2 py-1.5 md:grid-cols-[2fr_1fr_1fr_auto_1fr_1fr_1.5fr_auto]">
                          <div className="flex items-center gap-2">
                            <span className="text-xs font-semibold uppercase tracking-wide text-neutral-400">Subtotal</span>
                            {hasGroupActuals && grpActualTotal !== grpMonthly && (
                              <span className={`text-xs font-medium ${grpActualTotal > grpMonthly ? 'text-red-600' : 'text-emerald-600'}`}>
                                {grpActualTotal > grpMonthly
                                  ? `Over by ${currency(grpActualTotal - grpMonthly)}`
                                  : `Under by ${currency(grpMonthly - grpActualTotal)}`}
                              </span>
                            )}
                          </div>
                          <div /><div /><div />
                          <div className="text-sm font-semibold text-neutral-700">
                            {currency(Math.round(grpMonthly))}<span className="ml-0.5 text-xs font-normal text-neutral-400">/mo</span>
                          </div>
                          <div className="text-sm font-semibold text-neutral-700">
                            {currency(Math.round(grpAnnual))}<span className="ml-0.5 text-xs font-normal text-neutral-400">/yr</span>
                          </div>
                          <div /><div />
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <button
            onClick={addGroup}
            className="mt-4 w-full rounded-xl border border-dashed border-neutral-300 py-2.5 text-sm text-neutral-500 hover:border-neutral-400 hover:text-neutral-700"
          >
            + Add group
          </button>

          {/* Expense buffer + Grand total */}
          {categories.length > 0 && (() => {
            const bufferPct = appSettings?.expenseBufferPercent ?? 10;
            const bufferMo = totalMonthlyExpenses * bufferPct / 100;
            const plannedMo = totalMonthlyExpenses + bufferMo;
            return (
              <>
                {/* Buffer row */}
                <div className="mt-3 flex items-center justify-between rounded-xl bg-neutral-100 px-4 py-2.5">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-medium text-neutral-500">Expense Buffer</span>
                    <div className="flex items-center gap-1">
                      <NumberInput min={0} max={30} step={1}
                        defaultValue={bufferPct}
                        onBlur={(e) => {
                          if (!onUpdateAppSettings || !appSettings) return;
                          onUpdateAppSettings({ ...appSettings, expenseBufferPercent: Math.max(0, Math.min(30, parseInt(e.target.value) || 10)) });
                        }}
                        className="w-12 rounded border border-neutral-200 px-1.5 py-0.5 text-center text-xs font-medium text-neutral-700 focus:outline-none focus:ring-1 focus:ring-neutral-300"
                      />
                      <span className="text-xs text-neutral-400">%</span>
                    </div>
                    <InfoTooltip tip="A safety margin applied to your expenses for planning purposes. This does NOT affect your Monthly Surplus or bucket allocations — it's a display-only figure to show what your budget looks like with extra headroom for unexpected costs." />
                  </div>
                  <div className="flex items-baseline gap-4">
                    <span className="text-xs font-medium text-neutral-600">
                      {currency(Math.round(bufferMo))}<span className="ml-0.5 text-neutral-400">/mo</span>
                    </span>
                    <span className="text-xs font-medium text-neutral-600">
                      {currency(Math.round(bufferMo * 12))}<span className="ml-0.5 text-neutral-400">/yr</span>
                    </span>
                  </div>
                </div>

                {/* Grand total (includes buffer) */}
                <div className="mt-2 flex items-center justify-between rounded-xl border-2 border-neutral-200 bg-white px-4 py-3">
                  <div>
                    <span className="text-sm font-semibold text-neutral-700">Planned Total</span>
                    <span className="ml-1.5 text-[10px] text-neutral-400">(inc. {bufferPct}% buffer)</span>
                  </div>
                  <div className="flex items-baseline gap-4">
                    <span className="text-base font-semibold text-neutral-900">
                      {currency(Math.round(plannedMo))}<span className="ml-1 text-xs font-normal text-neutral-400">/mo</span>
                    </span>
                    <span className="text-base font-semibold text-neutral-900">
                      {currency(Math.round(plannedMo * 12))}<span className="ml-1 text-xs font-normal text-neutral-400">/yr</span>
                    </span>
                  </div>
                </div>
                <div className="mt-1 text-right text-[10px] text-neutral-400">
                  Raw expenses: {currency(Math.round(totalMonthlyExpenses))}/mo
                </div>
              </>
            );
          })()}
        </div>

        {/* ── 3. SPENDING BREAKDOWN (PIE CHART) ────────────────────────────────── */}
        <div className="rounded-2xl bg-neutral-50 p-5">
          <SectionHeader
            title="Spending breakdown"
            subtitle="A visual snapshot of where your money goes each month. Larger slices = bigger spending categories."
          />

          {pieSegments.length === 0 ? (
            <p className="py-6 text-center text-sm text-neutral-400">
              Add expenses above to see your breakdown.
            </p>
          ) : (
            <div className="flex flex-col items-start gap-8 md:flex-row md:items-center">
              {/* SVG pie */}
              <svg
                width="160" height="160" viewBox="0 0 160 160"
                className="shrink-0"
                aria-label="Spending breakdown pie chart"
              >
                {pieSegments.length === 1 ? (
                  <circle cx="80" cy="80" r="72" fill={PIE_COLORS[0]} />
                ) : (
                  pieSegments.map((seg, i) => (
                    <path
                      key={seg.id}
                      d={describeArc(80, 80, 72, seg.startAngle, seg.endAngle)}
                      fill={PIE_COLORS[i % PIE_COLORS.length]}
                    />
                  ))
                )}
                {/* Centre text */}
                <circle cx="80" cy="80" r="38" fill="white" />
                <text x="80" y="76" textAnchor="middle" fontSize="12" fontWeight="700" fill="#111111">
                  {currency(Math.round(totalMonthlyExpenses))}
                </text>
                <text x="80" y="91" textAnchor="middle" fontSize="10" fill="#888888">/mo total</text>
              </svg>

              {/* Legend */}
              <div className="grid flex-1 gap-x-6 gap-y-2 md:grid-cols-2">
                {pieSegments.map((seg, i) => (
                  <div key={seg.id} className="flex items-center gap-2.5 text-sm">
                    <span
                      className="h-3 w-3 shrink-0 rounded-sm"
                      style={{ background: PIE_COLORS[i % PIE_COLORS.length] }}
                    />
                    <span className="min-w-0 flex-1 truncate text-neutral-700">{seg.name}</span>
                    <span className="shrink-0 text-neutral-600">{currency(Math.round(seg.monthly))}</span>
                    <span className="w-10 shrink-0 text-right text-xs text-neutral-400">{seg.pct}%</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* ── 4. SUMMARY STAT BAR ──────────────────────────────────────────────── */}
        <div className="rounded-2xl bg-neutral-50 p-5">
          <SectionHeader
            title="Your monthly position"
            subtitle="The numbers that matter — what comes in, what goes out, and what's left over for you to allocate."
          />

          <div className="grid gap-4 md:grid-cols-3">
            {/* Monthly Income */}
            <div className="rounded-2xl bg-white p-5">
              <div className="text-sm text-neutral-500">Monthly Income</div>
              <div className="mt-2 text-2xl font-semibold text-neutral-900">{currency(Math.round(totalMonthlyIncome))}</div>
              <div className="mt-1 text-xs text-neutral-400">From income rows above</div>
            </div>

            {/* Monthly Expenses */}
            <div className="rounded-2xl bg-white p-5">
              <div className="text-sm text-neutral-500">Monthly Expenses</div>
              <div className="mt-2 text-2xl font-semibold text-neutral-900">{currency(Math.round(totalMonthlyExpenses))}</div>
              <div className="mt-1 text-xs text-neutral-400">Auto-calculated from categories</div>
            </div>

            {/* Monthly Surplus */}
            <div className="rounded-2xl bg-white p-5">
              <div className="text-sm text-neutral-500">Monthly Surplus</div>
              <div className={`mt-2 text-2xl font-semibold ${monthlySurplus >= 0 ? "text-emerald-700" : "text-red-600"}`}>
                {currency(Math.round(monthlySurplus))}
              </div>
              <div className="mt-1 text-xs text-neutral-400">Income minus expenses</div>
            </div>
          </div>

          {/* Budget vs actual variance callout */}
          {hasAnyActuals && (() => {
            const variance = totalActualExpenses - totalMonthlyExpenses;
            const isOver = variance > 0;
            return (
              <div className={`mt-4 flex items-center gap-4 rounded-xl border-l-4 px-4 py-3 text-sm ${isOver ? "border-red-500 bg-red-50" : "border-emerald-500 bg-emerald-50"}`}>
                <span className="text-neutral-600">
                  Budgeted: <span className="font-medium text-neutral-900">{currency(Math.round(totalMonthlyExpenses))}/mo</span>
                </span>
                <span className="text-neutral-400">·</span>
                <span className="text-neutral-600">
                  Actual: <span className="font-medium text-neutral-900">{currency(Math.round(totalActualExpenses))}/mo</span>
                </span>
                <span className="text-neutral-400">·</span>
                <span className={`font-semibold ${isOver ? "text-red-700" : "text-emerald-700"}`}>
                  {isOver ? "Over" : "Under"} by {currency(Math.round(Math.abs(variance)))}
                </span>
              </div>
            );
          })()}

          {/* Bucket allocation summary line */}
          <p className="mt-3 text-sm text-neutral-400">
            <span>{currency(Math.round(totalAllocated))} allocated across {allocatedBucketCount} bucket{allocatedBucketCount !== 1 ? "s" : ""}</span>
            <span className="mx-1.5">·</span>
            {remaining >= 0
              ? <span>{currency(Math.round(remaining))} still unallocated</span>
              : <span className="text-amber-600">{currency(Math.round(-remaining))} over-allocated</span>
            }
          </p>

          {/* Budget vs actual variance banner */}
          {hasAnyActuals && (
            <div className={`mt-3 rounded-lg px-3 py-2 text-sm font-medium ${totalActualExpenses > totalMonthlyExpenses ? 'bg-red-50 text-red-700' : 'bg-emerald-50 text-emerald-700'}`}>
              Budget vs actual: {currency(Math.abs(totalActualExpenses - totalMonthlyExpenses))}{' '}
              {totalActualExpenses > totalMonthlyExpenses ? 'over budget this month' : 'under budget this month'}
            </div>
          )}
        </div>

        </>)}

        {activeTab === "buckets" && (<>
        {/* ── BUCKETS TAB — Phase 6b redesign ──────────────────────────────────── */}

        {/* Section 1 — Header */}
        <div className="flex items-start justify-between">
          <div>
            <h2 className="flex items-center gap-1.5 text-lg font-semibold text-neutral-900">
              Buckets
              <InfoTooltip tip="A bucket is a named allocation of your monthly surplus. Use them to mentally (and physically, via bank account transfers) separate money for different purposes — travel, emergencies, property investment, etc." />
            </h2>
            <p className="mt-1 text-sm text-neutral-500">Divide your monthly surplus into named accounts.</p>
          </div>
        </div>

        {/* Section 2 — Surplus flow visualization */}
        <div className="rounded-2xl bg-neutral-50 p-5">
          <div className="text-center mb-4">
            <div className="text-[10px] font-medium uppercase tracking-wide text-neutral-400 flex items-center justify-center gap-1">
              Monthly Surplus
              <InfoTooltip tip="Your income minus all your personal expenses. This is what you have left over to allocate each month." />
            </div>
            <div className="text-2xl font-semibold text-neutral-900">{currency(Math.round(monthlySurplus))}<span className="text-sm font-normal text-neutral-400">/mo</span></div>
          </div>

          {settings.buckets.length === 0 ? (
            <div className="rounded-xl border-2 border-dashed border-neutral-200 px-6 py-8 text-center">
              <p className="text-sm text-neutral-500 mb-3">No buckets yet. Divide your surplus into named buckets — investments, emergency, travel, or anything else.</p>
              <button onClick={addBucket} className="rounded-xl bg-neutral-900 px-5 py-2 text-sm font-medium text-white hover:bg-neutral-700">
                <Plus className="inline h-4 w-4 mr-1 -mt-0.5" />Add your first bucket
              </button>
            </div>
          ) : (
            <>
              {/* Bucket cards grid */}
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                {settings.buckets.map((bucket) => {
                  const isProperty = bucket.type === "property";
                  return (
                    <button
                      key={bucket.id}
                      onClick={() => setExpandedBucketId((prev) => prev === bucket.id ? null : bucket.id)}
                      className={`relative flex flex-col justify-between rounded-2xl border bg-white p-4 text-left transition-all hover:shadow-md ${
                        isProperty ? "border-l-4 border-l-amber-400 border-t border-r border-b border-neutral-200" : "border-neutral-200"
                      } ${expandedBucketId === bucket.id ? "ring-2 ring-neutral-900" : ""}`}
                      style={{ minHeight: 140 }}
                    >
                      {isProperty && (
                        <span className="absolute top-2 right-2 flex items-center gap-1 rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-700">
                          <Lock className="h-3 w-3" /> FIXED
                        </span>
                      )}
                      {bucket.type !== "custom" && bucket.type !== "property" && (
                        <span className="absolute top-2 right-2 rounded-full bg-neutral-100 px-1.5 py-0.5 text-[10px] font-medium text-neutral-500 capitalize">
                          {bucket.type}
                        </span>
                      )}
                      <div>
                        <div className="flex items-center gap-1">
                          {isProperty && <Lock className="h-3 w-3 text-amber-500 shrink-0" />}
                          <span className="text-sm font-semibold text-neutral-900 truncate">{bucket.name}</span>
                        </div>
                        {bucket.description && (
                          <p className="mt-0.5 text-xs text-neutral-400 truncate">{bucket.description}</p>
                        )}
                      </div>
                      <div className="mt-2 text-lg font-semibold text-neutral-800">
                        {currency(Math.round(bucket.monthlyAmount))}
                        <span className="text-xs font-normal text-neutral-400">/mo</span>
                      </div>
                    </button>
                  );
                })}

                {/* Add bucket card */}
                <button
                  onClick={() => { addBucket(); }}
                  className="flex flex-col items-center justify-center rounded-2xl border-2 border-dashed border-neutral-200 text-neutral-400 hover:border-neutral-400 hover:text-neutral-600 transition-colors"
                  style={{ minHeight: 140 }}
                >
                  <Plus className="h-5 w-5 mb-1" />
                  <span className="text-xs font-medium">Add bucket</span>
                </button>
              </div>

              {/* Allocation summary */}
              <div className="mt-4 flex items-center justify-center gap-2 text-sm">
                <span className="text-neutral-500">Funded: <span className="font-medium text-neutral-800">{currency(Math.round(totalActuallyFunded))}</span> / {currency(Math.round(totalAllocated))} requested</span>
                <span className="text-neutral-300">·</span>
                <span className={remaining > 0 ? "text-emerald-600 font-medium" : "text-neutral-500 font-medium"}>
                  {remaining > 0 ? `${currency(Math.round(remaining))} unallocated surplus` : "Fully allocated"}
                </span>
              </div>
            </>
          )}
        </div>

        {/* Section 3 — Drag-to-reorder bucket list (waterfall priority) */}
        {settings.buckets.length > 0 && (
          <div className="rounded-2xl bg-neutral-50 p-5">
            <h3 className="flex items-center gap-1 text-sm font-semibold text-neutral-700 mb-3">
              Bucket Details
              <InfoTooltip tip="Buckets are funded top-to-bottom from your monthly surplus. Drag to reorder — higher buckets get funded first. If surplus runs out, lower buckets are partially funded or unfunded." />
            </h3>
            <DndContext sensors={bucketSensors} collisionDetection={closestCenter} onDragEnd={handleBucketDragEnd}>
              <SortableContext items={settings.buckets.map((b) => b.id)} strategy={verticalListSortingStrategy}>
                <div className="space-y-1">
                  {settings.buckets.map((bucket) => {
                    const isExpanded = expandedBucketId === bucket.id;
                    const isLocked = !!bucket.locked;
                    const alloc = bucketAllocations.find((a) => a.bucket.id === bucket.id) ?? { actual: bucket.monthlyAmount, fullyFunded: true, shortfall: 0, requested: bucket.monthlyAmount };
                    return (
                      <div key={bucket.id} className="group/row">
                        <SortableBucketRow
                          bucket={bucket}
                          isExpanded={isExpanded}
                          onToggle={() => setExpandedBucketId((prev) => prev === bucket.id ? null : bucket.id)}
                          allocation={alloc}
                          onRemove={!isLocked ? () => removeBucket(bucket.id) : undefined}
                        >
                          <div className="border-t border-neutral-100 px-4 py-4 space-y-4">
                            {isLocked && (
                              <p className="text-xs text-amber-700 bg-amber-50 rounded-lg px-3 py-2">
                                This bucket represents money you're allocating to your property portfolio from your monthly surplus. It's a fixed bucket and can't be deleted — but you can drag it to change its priority.
                              </p>
                            )}
                            {!alloc.fullyFunded && (
                              <p className="text-xs text-amber-700 bg-amber-50 rounded-lg px-3 py-2">
                                {alloc.actual === 0
                                  ? `Unfunded — ${currency(Math.round(alloc.requested))}/mo requested but surplus is exhausted. Drag this bucket higher to fund it first.`
                                  : `Partially funded — ${currency(Math.round(alloc.actual))}/mo of ${currency(Math.round(alloc.requested))}/mo requested. ${currency(Math.round(alloc.shortfall))}/mo short.`}
                              </p>
                            )}
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                              <div>
                                <label className="text-[10px] font-medium uppercase tracking-wide text-neutral-400">Bucket Name</label>
                                <input
                                  type="text"
                                  defaultValue={bucket.name}
                                  disabled={isLocked}
                                  onBlur={(e) => { if (!isLocked) updateBucket(bucket.id, { name: e.target.value }); }}
                                  className={`mt-1 w-full rounded-lg border border-neutral-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-neutral-300 ${isLocked ? "bg-neutral-50 text-neutral-500" : ""}`}
                                />
                              </div>
                              <div>
                                <label className="flex items-center gap-1 text-[10px] font-medium uppercase tracking-wide text-neutral-400">
                                  Purpose
                                  <InfoTooltip tip="A short description of what this bucket is for. Used to remind yourself why the money is being set aside." />
                                </label>
                                <input
                                  type="text"
                                  defaultValue={bucket.description ?? ""}
                                  onBlur={(e) => updateBucket(bucket.id, { description: e.target.value })}
                                  placeholder="What is this bucket for?"
                                  className="mt-1 w-full rounded-lg border border-neutral-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-neutral-300"
                                />
                              </div>
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                              <div>
                                <label className="text-[10px] font-medium uppercase tracking-wide text-neutral-400">Monthly Amount (requested)</label>
                                <div className="mt-1 flex items-center gap-1">
                                  <span className="text-xs text-neutral-400">$</span>
                                  <NumberInput min={0}
                                    defaultValue={bucket.monthlyAmount || ""}
                                    onBlur={(e) => updateBucket(bucket.id, { monthlyAmount: parseFloat(e.target.value) || 0 })}
                                    className="w-full rounded-lg border border-neutral-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-neutral-300"
                                  />
                                </div>
                              </div>
                              {!isLocked && (
                                <div>
                                  <label className="flex items-center gap-1 text-[10px] font-medium uppercase tracking-wide text-neutral-400">
                                    Type
                                    <InfoTooltip tip="Buckets can be categorised for organisation. Does not affect calculations — purely labelling." />
                                  </label>
                                  <select
                                    defaultValue={bucket.type}
                                    onChange={(e) => updateBucket(bucket.id, { type: e.target.value as BudgetBucket["type"] })}
                                    className="mt-1 w-full rounded-lg border border-neutral-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-neutral-300"
                                  >
                                    <option value="custom">Custom</option>
                                    <option value="emergency">Emergency</option>
                                    <option value="lifestyle">Lifestyle</option>
                                    <option value="savings">Savings</option>
                                  </select>
                                </div>
                              )}
                            </div>
                            {/* Account details */}
                            <div>
                              <div className="text-[10px] font-medium uppercase tracking-wide text-neutral-400 mb-2">Account Details</div>
                              <div className="grid grid-cols-2 gap-3">
                                <div>
                                  <label className="text-[10px] text-neutral-400">Bank / Institution</label>
                                  <input type="text" defaultValue={bucket.accountBankName ?? ""} onBlur={(e) => updateBucket(bucket.id, { accountBankName: e.target.value })}
                                    className="mt-0.5 w-full rounded-lg border border-neutral-200 px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-neutral-300" />
                                </div>
                                <div>
                                  <label className="text-[10px] text-neutral-400">Account Name</label>
                                  <input type="text" defaultValue={bucket.accountName ?? ""} onBlur={(e) => updateBucket(bucket.id, { accountName: e.target.value })}
                                    className="mt-0.5 w-full rounded-lg border border-neutral-200 px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-neutral-300" />
                                </div>
                                <div>
                                  <label className="text-[10px] text-neutral-400">BSB</label>
                                  <input type="text" defaultValue={bucket.accountBSB ?? ""} onBlur={(e) => updateBucket(bucket.id, { accountBSB: e.target.value })}
                                    className="mt-0.5 w-full rounded-lg border border-neutral-200 px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-neutral-300" />
                                </div>
                                <div>
                                  <label className="text-[10px] text-neutral-400">Account Number</label>
                                  <input type="text" defaultValue={bucket.accountNumber ?? ""} onBlur={(e) => updateBucket(bucket.id, { accountNumber: e.target.value })}
                                    className="mt-0.5 w-full rounded-lg border border-neutral-200 px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-neutral-300" />
                                </div>
                              </div>
                              <p className="mt-2 text-[10px] text-neutral-400">Transfers will be automated to this account (future)</p>
                            </div>
                          </div>
                        </SortableBucketRow>
                      </div>
                    );
                  })}
                </div>
              </SortableContext>
            </DndContext>
          </div>
        )}
        </>)}

      </div>

      {/* ── IMPORT MODAL ─────────────────────────────────────────────────────── */}
      {showImportModal && (
        <ImportModal
          categories={categories}
          onImport={(updates) => {
            updateCats(
              categories.map((cat) => {
                const add = updates[cat.category] ?? 0;
                if (add === 0) return cat;
                return { ...cat, actual: (cat.actual ?? 0) + add };
              })
            );
          }}
          onClose={() => setShowImportModal(false)}
        />
      )}

      {/* ── EXPORT MODAL ─────────────────────────────────────────────────────── */}
      {showExportModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4">
          <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl">
            <h3 className="text-lg font-semibold text-neutral-900">Export budget</h3>

            <div className="mt-4">
              <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-neutral-500">Format</p>
              <div className="flex gap-4">
                {(["pdf", "csv"] as const).map((fmt) => (
                  <label key={fmt} className="flex cursor-pointer items-center gap-2 text-sm">
                    <input type="checkbox" checked={exportFmt[fmt]} onChange={(e) => setExportFmt((p) => ({ ...p, [fmt]: e.target.checked }))} className="rounded" />
                    {fmt.toUpperCase()}
                  </label>
                ))}
              </div>
            </div>

            <div className="mt-4">
              <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-neutral-500">Expense grouping</p>
              <div className="space-y-1.5">
                {(["current", "broker", "custom"] as const).map((opt) => (
                  <label key={opt} className="flex cursor-pointer items-center gap-2 text-sm">
                    <input type="radio" name="grouping" value={opt} checked={exportGrouping === opt} onChange={() => setExportGrouping(opt)} />
                    {opt === "current" ? "Use my current groups" : opt === "broker" ? "Broker grouping" : "Custom"}
                  </label>
                ))}
              </div>
              {exportGrouping === "broker"  && <p className="mt-2 text-xs text-neutral-400">Groups: Living Expenses · Housing · Transport · Insurance · Debt Repayments · Investment Costs · Other</p>}
              {exportGrouping === "custom"  && <p className="mt-2 text-xs italic text-neutral-400">Custom grouping coming soon.</p>}
            </div>

            <div className="mt-4">
              <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-neutral-500">Columns</p>
              <div className="flex flex-wrap gap-3">
                {(["budget", "actual", "variance", "notes", "annual"] as const).map((col) => (
                  <label key={col} className="flex cursor-pointer items-center gap-1.5 text-sm capitalize">
                    <input type="checkbox" checked={exportCols[col]} onChange={(e) => setExportCols((p) => ({ ...p, [col]: e.target.checked }))} className="rounded" />
                    {col}
                  </label>
                ))}
              </div>
            </div>

            <div className="mt-4">
              <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-neutral-500">Include sections</p>
              <div className="flex gap-4">
                {(["income", "expenses", "buckets"] as const).map((sec) => (
                  <label key={sec} className="flex cursor-pointer items-center gap-1.5 text-sm capitalize">
                    <input type="checkbox" checked={exportSections[sec]} onChange={(e) => setExportSections((p) => ({ ...p, [sec]: e.target.checked }))} className="rounded" />
                    {sec === "buckets" ? "Buckets / Savings" : sec}
                  </label>
                ))}
              </div>
            </div>

            <div className="mt-6 flex gap-2">
              {exportFmt.pdf && (
                <button onClick={exportPDF} className="rounded-xl bg-neutral-900 px-4 py-2 text-sm font-medium text-white hover:bg-neutral-700">Export PDF</button>
              )}
              {exportFmt.csv && (
                <button onClick={exportCSV} className="rounded-xl bg-neutral-900 px-4 py-2 text-sm font-medium text-white hover:bg-neutral-700">Export CSV</button>
              )}
              <button onClick={() => setShowExportModal(false)} className="ml-auto rounded-xl border border-neutral-200 px-4 py-2 text-sm text-neutral-600 hover:bg-neutral-50">Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* ── BUDGET IMPORT EXPLAINER MODAL ────────────────────────────────────── */}
      {showBudgetImportModal && onOpenImport && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-md rounded-2xl bg-white shadow-2xl">
            <div className="px-6 pt-6 pb-2">
              <h3 className="text-lg font-semibold text-neutral-900">Import your budget</h3>
            </div>
            <div className="px-6 py-4 space-y-4">
              <p className="text-sm text-neutral-600 leading-relaxed">
                AusProp will read your spreadsheet and extract your income and expense categories.
              </p>
              <p className="text-sm text-neutral-600 leading-relaxed">
                You'll review everything before anything is saved.
              </p>
              <p className="text-sm font-medium text-neutral-800">Choose how to handle your existing budget:</p>
              <ul className="space-y-2 text-sm text-neutral-600">
                <li className="flex items-start gap-2">
                  <span className="mt-0.5 font-semibold text-neutral-800 shrink-0">Replace existing</span>
                  <span>— removes all current categories and replaces them with yours. Your category names, amounts and order are preserved. You can still edit, add and reorder after import.</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="mt-0.5 font-semibold text-neutral-800 shrink-0">Merge with existing</span>
                  <span>— keeps your current budget and adds new categories from your spreadsheet that don't already exist by name.</span>
                </li>
              </ul>
            </div>
            <div className="flex items-center justify-between gap-3 px-6 py-4 border-t border-neutral-100">
              <button
                onClick={() => setShowBudgetImportModal(false)}
                className="text-sm text-neutral-500 hover:text-neutral-700 transition-colors"
              >
                Cancel
              </button>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => { setShowBudgetImportModal(false); onOpenImport('merge'); }}
                  className="rounded-full border border-[#C47A2F] px-5 py-2 text-sm font-medium text-[#C47A2F] hover:bg-[#FFF7ED] transition-colors"
                >
                  Merge with existing
                </button>
                <button
                  onClick={() => { setShowBudgetImportModal(false); onOpenImport('replace'); }}
                  className="rounded-full bg-[#C47A2F] px-5 py-2 text-sm font-medium text-white hover:bg-[#A8601F] transition-colors"
                >
                  Replace existing
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
